/* ============
 * Create Page
 * ============
 *
 * This page creates transfers.
 */

import { mapGetters } from 'vuex';
import transferService from './../../../../services/transfer';
import transferTransformer from './../../../../transformers/transfer';
import productService from './../../../../services/product';
import warehouseService from './../../../../services/warehouse';
import batchService from './../../../../services/batch';
import batchTransformer from './../../../../transformers/batch';
import transactionTypeService from './../../../../services/transactionType';
import Forms from './../../../../utils/forms/forms';
import store from './../../../../store';

export default {
  data() {
    return {
      formBasicInformation: new Forms({
        transferType: {
          value: '',
          type: 'select',
        },
        date: {
          value: '',
          type: 'date',
        },
        fromWarehouseId: {
          value: '',
          type: 'select',
        },
        toWarehouseId: {
          value: '',
          type: 'select',
        },
        batchId: {
          value: '',
          type: 'select'
        },
        comments: {
          value: '',
          type: 'textarea',
        },
      }),
      loading: true,
      tabIndex: 'first',
      fields: {
        productId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        batchId: {
          value: '',
          type: 'select',
        },
        qty: {
          value: '0',
          type: 'text',
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      currentStockProducts: 'currentStockProductsCustom',
      transactionTypes: 'allTransactionTypes',
      warehouses: 'allWarehouses',
      batches: 'allBatches',
      account: 'account',
    }),

    additionalData() {
      const Obj = Object.create({});
      Obj.fromWarehouseId = this.formBasicInformation.fromWarehouseId;
      return Obj;
    },

  },

  watch: {
    $route() {
      this.warehouseTarget();
    },

    transactionTypes(transactionTypes) {
      this.formBasicInformation.setOptions('transferType',
        transactionTypes.filter(type => type.relations.filter(relation => relation.includes('transfer')).length).map(transactionType => ({
          id: transactionType.id,
          name: transactionType.description,
        }))
      );
    },
    warehouses(warehouses) {
      const data = warehouses.items.filter(item => !item.isSharedToYou);
      this.formBasicInformation.setOptions('fromWarehouseId', data.map(item => ({ id: item.id, name: item.name + (item.isLocked ? this.$t('static.warehouse.info.locked') : '') })));
      this.formBasicInformation.setOptions('toWarehouseId', data.map(item => ({ id: item.id, name: item.name + (item.isLocked ? this.$t('static.warehouse.info.locked') : '') })));
    },
    batches(batches) {
      if ('batchId' in this.formBasicInformation.hidden) {
        this.formBasicInformation.setOptions('batchId', batches.map(batch => ({
          id: batch.id,
          name: batch.name ? `${batch.batchNumber}:${batch.name}` : batch.batchNumber,
        })));
      }
    },

    /**
     * Todo:
     * - Load warehouse products if fromWarehouseId is set.
     * - Avoid setting the same warehouse on both warehouse options.
     * - Ideally disable the selected fromWarehouseID in the toWarehouseID.
     */
    'formBasicInformation.fromWarehouseId': function (fromWarehouseId) {
      if (this.formBasicInformation.toWarehouseId === fromWarehouseId) {
        this.formBasicInformation.toWarehouseId = '';
      }
      store.dispatch('setStockSelectedWarehouse', fromWarehouseId);
    },

    'formBasicInformation.toWarehouseId': function (toWarehouseId) {
      if (this.formBasicInformation.fromWarehouseId === toWarehouseId) {
        this.formBasicInformation.fromWarehouseId = '';
      }
      store.dispatch('setStockSelectedWarehouse', toWarehouseId);
    },

    'formBasicInformation.batchId': function (batchId) {
      if (batchId) {
        const batch = this.batches.find(item => item.id === batchId);
        const products = [];
        if (batch.recipe && batch.recipe.products) {
          this.loading = true;

          batch.recipe.products.forEach((product) => {
            if (!product.variations.length) {
              products.push({
                id: product.id,
                name: product.name,
                qty: '',
                product,
                productId: product,
                batchId: batch.id,
              });
            } else {
              product.variations.forEach((childProduct) => {
                products.push({
                  id: childProduct.id,
                  name: childProduct.name,
                  qty: '',
                  product: childProduct,
                  productId: childProduct,
                  batchId: batch.id,
                });
              });
            }
          });
          if (products) {
            store.dispatch('allCurrentStockProductsCustom', products);
          }
          this.loading = false;
        }
      }
    },

    /**
     * Todo:
     * - Load in tranfer types.
     * - Hide all fields if transfer type is not selected.
     * - Set conditions on which field to show and which to hide based on the transfer type selected.
     */
    'formBasicInformation.transferType': function (typeId) {
      const type = this.transactionTypes.filter(t => (
        typeId.toString() === t.id.toString()
      ))[0];

      // Default
      this.formBasicInformation.hideField('date');
      this.formBasicInformation.hideField('fromWarehouseId');
      this.formBasicInformation.hideField('toWarehouseId');
      this.formBasicInformation.hideField('batchId');
      this.formBasicInformation.hideField('comments');

      // Transfer Between Warehouse
      if (type.code === 'TW') {
        this.formBasicInformation.showField('toWarehouseId');
        this.formBasicInformation.showField('fromWarehouseId');
        this.formBasicInformation.showField('comments');
      }

      if (type.code === 'TTB') {
        this.formBasicInformation.showField('fromWarehouseId');
        this.formBasicInformation.showField('comments');
        this.formBasicInformation.showField('batchId');
        if (this.account.allowProducerBatches) {
          this.formBasicInformation.setExtraAttr('batchId', {
            hasButton: true,
            buttonLabel: this.$t('static.batches.create.title'),
            buttonHandler: this.addBatch,
          });
        }
      }

      // From Batch
      // Transfer From Other Liquor Licencee
      // Return Of Stock
      if (type.code === 'TB' ||
        type.code === 'TL' ||
        type.code === 'RS'
      ) {
        this.formBasicInformation.showField('toWarehouseId');
        this.formBasicInformation.hideField('fromWarehouseId');
        this.formBasicInformation.showField('comments');
        if (type.code === 'TB') {
          this.formBasicInformation.showField('batchId');
          this.formBasicInformation.setExtraAttr('batchId', {});
          if (this.$route.query.batch) {
            this.formBasicInformation.batchId = this.$route.query.batch;
          }
        }
      }
      if ('batchId' in this.formBasicInformation.hidden) {
        this.formBasicInformation.setOptions('batchId', []);
      } else {
        this.formBasicInformation.setOptions('batchId', this.batches.map(batch => ({
          id: batch.id,
          name: batch.name ? `${batch.batchNumber}:${batch.name}` : batch.batchNumber,
        })));
      }
    },
  },

  created() {
    this.warehouseTarget();
    this.loading = false;
    this.formBasicInformation.hideField('date');
    this.formBasicInformation.hideField('fromWarehouseId');
    this.formBasicInformation.hideField('toWarehouseId');
    this.formBasicInformation.hideField('comments');
  },

  methods: {
    warehouseTarget() {
      this.formBasicInformation.setFieldValue('fromWarehouseId', this.$router.currentRoute.hash.substring(1));
    },

    /**
     * Method to store the transfer
     */
    store() {
      this.loading = false;
      this.formBasicInformation.loading = true;
      const products = this.currentStockProducts.filter(p => p.qty > 0);
      const type = this.transactionTypes.filter(t => (
        this.formBasicInformation.transferType.toString() === t.id.toString()
      ))[0];
      const data = Object.assign({}, this.formBasicInformation.data(), { products });
      if (type.code === 'TTB') {
        data.toWarehouseId = this.formBasicInformation.fromWarehouseId;
      }
      const transfer = transferTransformer.send(data);
      transferService.store(transfer)
        .then((response) => {
          this.$message({
            message: this.$t('static.transfer.create.success'),
            type: 'success',
          });
          if (response.data && response.data.transfer_order_id) {
            this.$router.push({
              name: 'orders.edit',
              params: { id: response.data.transfer_order_id },
            });
          } else {
            this.$router.push({
              name: 'inventory.transfer.index',
            });
          }
        }).catch((errors) => {
          this.formBasicInformation.loading = false;
          this.formBasicInformation.recordErrors(errors);
          const message = errors.message;
          if (errors.message) {
            this.$message({
              showClose: true,
              message,
              customClass: 'error',
            });
          }
        });
    },
    searchMethod(search, field) {
      return Promise.all([
        productService.searchAll({ search }),
      ]);
    },
    addBatch(product) {
      this.$prompt(`${this.$t('form.batches.batchNumber.label')}`, this.$t('static.batches.create.title'), {
        confirmButtonText: this.$t('button.save'),
        cancelButtonText: this.$t('button.cancel'),
      }).then(({
        value,
      }) => {
        this.batchLoading = true;
        const batch = batchTransformer.send({
          batchNumber: value,
          status: 'completed',
          custom: 1,
        });
        batchService.store(batch)
        .then((data) => {
          store.dispatch('addBatch', data);
          this.formBasicInformation.setOptions('batchId', this.batches.map(b => ({
            id: b.id,
            name: `${b.batchNumber}:${b.name}`,
          })));
          this.$message({
            message: this.$t('static.batches.create.success'),
            type: 'success',
          });
        }).catch(() => {
          this.batchLoading = false;
          this.$message({
            message: this.$t('static.batches.create.error'),
            type: 'error',
          });
        });
      });
    },
  },
  mounted() {
    this.loading = true;
    store.dispatch('allCurrentStockProductsCustom', []);
    store.dispatch('setStockSelectedWarehouse', 0);
    Promise.all([
      warehouseService.all(),
      batchService.all(),
      transactionTypeService.all(),
    ]).then(() => {
      // store.dispatch('setStockSelectedWarehouse', 0);
      store.dispatch('RefreshStockProductsCustom');
      if (this.$route.query.batch) {
        const batchTransfer = this.transactionTypes.find(t => t.code === 'TB');
        if (batchTransfer) {
          this.formBasicInformation.transferType = batchTransfer.id;
        }
      } else {
        this.loading = false;
      }
    });
  },
  components: {
    VForm: require('./../../../../components/form/form.vue'),
    VMessage: require('./../../../../components/form/message/message.vue'),
    VProductsTable: require('./../../../../components/productTableWithCustomForm/productTableWithCustomForm.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/transfer/create/create.js